<template>
  <div class="subscription-page">
    <header class="nav-bar">
      <button class="back-btn" type="button" :aria-label="$t('common.back')" @click="goBack">
        &lsaquo;
      </button>
      <h1 class="nav-title">{{ $t('subscription.title') }}</h1>
      <div class="nav-placeholder"></div>
    </header>

    <main class="main-content">
      <section class="hero">
        <span class="hero-badge">VIP</span>
        <h2 class="hero-title">{{ $t('profile.subscriptionTitle') }}</h2>
        <p class="hero-description">{{ $t('profile.subscriptionDesc') }}</p>
      </section>

      <div v-if="loading" class="state-tip">{{ $t('subscription.loading') }}</div>

      <div v-else-if="!vipPackages.length" class="state-tip">
        {{ $t('subscription.empty') }}
      </div>

      <section v-else class="package-list" aria-label="VIP packages">
        <button
          v-for="item in vipPackages"
          :key="item.id"
          class="package-card"
          :class="{ selected: selectedPackageId === item.id }"
          type="button"
          @click="selectPackage(item)"
        >
          <span v-if="item.default == 1" class="recommended-badge">
            {{ $t('video.subscriptionDefault') }}
          </span>

          <span class="selection-mark" aria-hidden="true"></span>

          <span class="package-content">
            <span class="package-title">{{ item.title }}</span>
            <span class="package-description">
              {{ item.product_name || formatDays(item.number_days) }}
            </span>
            <span v-if="Number(item.give_number_days) > 0" class="bonus-days">
              +{{ formatDays(item.give_number_days) }}
            </span>
          </span>

          <span class="package-price">
            <strong>{{ formatAmount(item.amount) }}</strong>
            <small>{{ item.currency_symbol || currencySymbol }}</small>
          </span>
        </button>
      </section>

      <div v-if="selectedPackage" class="purchase-spacer"></div>
    </main>

    <footer v-if="selectedPackage" class="purchase-bar">
      <div class="purchase-content">
        <div class="purchase-summary">
          <strong>{{ selectedPackage.title }}</strong>
          <span>{{ formatDays(totalDays(selectedPackage)) }}</span>
        </div>

        <button
          class="purchase-btn"
          type="button"
          :disabled="activeMember"
          @click="openPaymentSheet"
        >
          <strong>{{ selectedPackage.currency_symbol || currencySymbol }}{{ formatAmount(selectedPackage.amount) }}</strong>
        </button>
      </div>
    </footer>

    <transition name="sheet-fade">
      <div v-if="paymentSheetVisible" class="payment-overlay" @click.self="closePaymentSheet">
        <section class="payment-sheet" role="dialog" aria-modal="true">
          <div class="sheet-handle" aria-hidden="true"></div>

          <button class="sheet-close" type="button" aria-label="Close" @click="closePaymentSheet">
            &times;
          </button>

          <div class="sheet-icon">VIP</div>
          <h2 class="sheet-title">{{ selectedPackage.title }}</h2>
          <p class="sheet-subtitle">{{ selectedPackage.product_name }}</p>

          <div class="sheet-details">
            <div class="sheet-detail-row">
              <span>{{ $t('video.subscriptionDays', { days: totalDays(selectedPackage) }) }}</span>
              <strong>{{ formatDays(totalDays(selectedPackage)) }}</strong>
            </div>
            <div class="sheet-detail-row total">
              <span>Total</span>
              <strong>
                {{ selectedPackage.currency_symbol || currencySymbol }}
                {{ formatAmount(selectedPackage.amount) }}
              </strong>
            </div>
          </div>

          <button
            class="sheet-confirm-btn"
            type="button"
            :disabled="paymentOpening || activeMember"
            @click="handlePayment"
          >
            <span v-if="paymentOpening" class="payment-spinner" aria-hidden="true"></span>
            <template v-else>
              {{ selectedPackage.currency_symbol || currencySymbol }}
              {{ formatAmount(selectedPackage.amount) }}
            </template>
          </button>

          <p class="sheet-note">{{ $t('profile.subscriptionDesc') }}</p>
        </section>
      </div>
    </transition>
  </div>
</template>

<script>
import { Toast } from 'vant';
import { getVipPackages,buyVip } from '@/apis/recharge';
import { isActiveMember } from '@/utils/member';

export default {
  name: 'SubscriptionPage',
  data() {
    return {
      currencySymbol: 'Rp',
      vipPackages: [],
      selectedPackageId: null,
      loading: false,
      paymentOpening: false,
      paymentSheetVisible: false
    };
  },
  computed: {
    activeMember() {
      const userInfo = this.$getUserInfo ? this.$getUserInfo() : null;
      return isActiveMember(userInfo);
    },
    selectedPackage() {
      return this.vipPackages.find(item => item.id === this.selectedPackageId) || null;
    }
  },
  mounted() {
    this.loadVipPackages();
  },
  methods: {
    async loadVipPackages() {
      if (this.loading) return;

      this.loading = true;

      try {
        const res = await getVipPackages({ currency_symbol: this.currencySymbol });
        const packages = res && res.code === 1 && Array.isArray(res.data) ? res.data : [];

        this.vipPackages = packages
          .slice()
          .sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0));

        const defaultPackage = this.vipPackages.find(item => item.default == 1);
        this.selectedPackageId = defaultPackage
          ? defaultPackage.id
          : this.vipPackages.length
            ? this.vipPackages[0].id
            : null;
      } catch (err) {
        console.error('getVipPackages error:', err);
        Toast(this.$t('subscription.loadFail'));
      } finally {
        this.loading = false;
      }
    },
    selectPackage(item) {
      this.selectedPackageId = item.id;
    },
    openPaymentSheet() {
      if (this.activeMember) return;
      if (!this.selectedPackage) return;
      this.paymentSheetVisible = true;
    },
    closePaymentSheet() {
      if (this.paymentOpening) return;
      this.paymentSheetVisible = false;
    },
    async handlePayment() {
      if (this.activeMember) return;
      if (!this.selectedPackage || this.paymentOpening) return;

      this.paymentOpening = true;

      try {
        const res = await buyVip({
          package_id: this.selectedPackage.id,
          back_url: window.location.href
        });

        if (!res || res.code !== 1) {
          Toast(res?.msg || this.$t('video.createSubscriptionOrderFail'));
          return;
        }

        const orderData = res.data || {};
        this.$router.push("/checkout/checkout?order_no=" + orderData.out_trade_no);
        // const payUrl = orderData.pay_url || '';

        // if (!payUrl) {
        //   console.log('vip pay_url is empty', orderData);
        //   Toast(this.$t('recharge.rechargeError'));
        //   return;
        // }

        // window.location.href = payUrl;
      } catch (err) {
        console.error('buyVip error:', err);
        Toast(this.$t('recharge.rechargeError'));
      } finally {
        this.paymentOpening = false;
      }
    },
    totalDays(item) {
      return Number(item.number_days || 0) + Number(item.give_number_days || 0);
    },
    formatDays(days) {
      return this.$t('video.subscriptionDays', { days: Number(days || 0) });
    },
    formatAmount(amount) {
      return Number(amount || 0).toLocaleString('id-ID');
    },
    goBack() {
      this.$router.back();
    }
  }
};
</script>

<style scoped>
.subscription-page {
  width: 100%;
  max-width: 450px;
  min-height: 100vh;
  margin: 0 auto;
  background: #f5f5f7;
  color: #1d1d1f;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif;
}

.nav-bar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 14px;
  background: rgba(245, 245, 247, 0.88);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  backdrop-filter: saturate(180%) blur(18px);
}

.back-btn {
  width: 36px;
  height: 36px;
  padding: 0 0 4px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #0071e3;
  font-size: 38px;
  font-weight: 300;
  line-height: 32px;
}

.back-btn:active {
  background: rgba(0, 113, 227, 0.08);
}

.nav-title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.nav-placeholder {
  width: 36px;
}

.main-content {
  padding: 22px 16px 40px;
}

.hero {
  padding: 14px 4px 26px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  background: #1d1d1f;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.hero-title {
  margin: 12px 0 5px;
  font-size: 30px;
  font-weight: 700;
  line-height: 1.12;
  letter-spacing: -0.04em;
}

.hero-description {
  margin: 0;
  color: #6e6e73;
  font-size: 15px;
  line-height: 1.45;
}

.state-tip {
  padding: 72px 0;
  color: #86868b;
  font-size: 14px;
  text-align: center;
}

.package-list {
  display: grid;
  gap: 12px;
}

.package-card {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 112px;
  padding: 20px 18px;
  overflow: hidden;
  border: 1.5px solid transparent;
  border-radius: 20px;
  background: #fff;
  color: #1d1d1f;
  font: inherit;
  text-align: left;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  transition: border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
}

.package-card:active {
  transform: scale(0.985);
}

.package-card.selected {
  border-color: #0071e3;
  box-shadow: 0 8px 24px rgba(0, 113, 227, 0.12);
}

.recommended-badge {
  position: absolute;
  top: 0;
  right: 18px;
  padding: 5px 10px 6px;
  border-radius: 0 0 9px 9px;
  background: #0071e3;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
}

.selection-mark {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin-right: 14px;
  border: 1.5px solid #c7c7cc;
  border-radius: 50%;
  box-sizing: border-box;
}

.selected .selection-mark {
  border: 5px solid #0071e3;
}

.package-content {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
}

.package-title {
  padding-right: 10px;
  font-size: 16px;
  font-weight: 650;
  line-height: 1.3;
  letter-spacing: -0.015em;
}

.package-description {
  margin-top: 5px;
  color: #6e6e73;
  font-size: 13px;
  line-height: 1.35;
}

.bonus-days {
  align-self: flex-start;
  margin-top: 7px;
  padding: 3px 7px;
  border-radius: 999px;
  background: #e8f3ff;
  color: #0071e3;
  font-size: 10px;
  font-weight: 600;
}

.package-price {
  display: flex;
  flex: 0 0 auto;
  margin-left: 10px;
  flex-direction: column;
  align-items: flex-end;
}

.package-price strong {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.03em;
}

.package-price small {
  margin-top: 3px;
  color: #86868b;
  font-size: 11px;
  font-weight: 500;
}

.purchase-spacer {
  height: 104px;
}

.purchase-bar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 20;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
  background: rgba(245, 245, 247, 0.9);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  backdrop-filter: saturate(180%) blur(20px);
}

.purchase-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 418px;
  margin: 0 auto;
  gap: 14px;
}

.purchase-summary {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.purchase-summary strong {
  overflow: hidden;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.purchase-summary span {
  margin-top: 4px;
  color: #86868b;
  font-size: 11px;
}

.purchase-btn {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 142px;
  height: 50px;
  padding: 0 22px;
  gap: 2px;
  border: 0;
  border-radius: 14px;
  background: #0071e3;
  color: #fff;
  font: inherit;
  box-shadow: 0 4px 12px rgba(0, 113, 227, 0.22);
  transition: transform 0.16s ease, background 0.16s ease;
}

.purchase-btn:active {
  background: #0068d1;
  transform: scale(0.98);
}

.purchase-btn:disabled {
  opacity: 0.65;
}

.purchase-btn strong {
  font-size: 17px;
  font-weight: 650;
  letter-spacing: -0.02em;
}

.purchase-btn span {
  display: block;
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  opacity: 0.82;
}

.payment-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: payment-spin 0.7s linear infinite;
}

.payment-overlay {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 12px;
  background: rgba(0, 0, 0, 0.34);
  backdrop-filter: blur(3px);
  box-sizing: border-box;
}

.payment-sheet {
  position: relative;
  width: 100%;
  max-width: 426px;
  padding: 12px 18px calc(18px + env(safe-area-inset-bottom));
  border-radius: 28px;
  background: rgba(250, 250, 252, 0.98);
  color: #1d1d1f;
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.24);
  box-sizing: border-box;
}

.sheet-handle {
  width: 36px;
  height: 5px;
  margin: 0 auto 20px;
  border-radius: 999px;
  background: #d1d1d6;
}

.sheet-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 30px;
  height: 30px;
  padding: 0 0 2px;
  border: 0;
  border-radius: 50%;
  background: #e9e9eb;
  color: #6e6e73;
  font-size: 22px;
  line-height: 28px;
}

.sheet-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin: 0 auto;
  border-radius: 14px;
  background: linear-gradient(145deg, #252527, #000);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.2);
}

.sheet-title {
  margin: 16px 0 4px;
  font-size: 21px;
  font-weight: 700;
  text-align: center;
  letter-spacing: -0.03em;
}

.sheet-subtitle {
  min-height: 18px;
  margin: 0;
  color: #86868b;
  font-size: 13px;
  text-align: center;
}

.sheet-details {
  margin: 22px 0 16px;
  overflow: hidden;
  border-radius: 14px;
  background: #fff;
}

.sheet-detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 48px;
  margin-left: 16px;
  padding-right: 16px;
  border-bottom: 1px solid #e5e5ea;
  font-size: 13px;
}

.sheet-detail-row:last-child {
  border-bottom: 0;
}

.sheet-detail-row span {
  color: #6e6e73;
}

.sheet-detail-row strong {
  font-size: 13px;
  font-weight: 600;
  text-align: right;
}

.sheet-detail-row.total strong {
  font-size: 16px;
}

.sheet-confirm-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 52px;
  border: 0;
  border-radius: 14px;
  background: #0071e3;
  color: #fff;
  font: inherit;
  font-size: 16px;
  font-weight: 600;
  box-shadow: 0 5px 14px rgba(0, 113, 227, 0.22);
}

.sheet-confirm-btn:active {
  background: #0068d1;
  transform: scale(0.99);
}

.sheet-confirm-btn:disabled {
  opacity: 0.65;
}

.sheet-note {
  margin: 12px 8px 0;
  color: #86868b;
  font-size: 11px;
  line-height: 1.4;
  text-align: center;
}

.sheet-fade-enter-active,
.sheet-fade-leave-active {
  transition: opacity 0.22s ease;
}

.sheet-fade-enter-active .payment-sheet,
.sheet-fade-leave-active .payment-sheet {
  transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1);
}

.sheet-fade-enter,
.sheet-fade-leave-to {
  opacity: 0;
}

.sheet-fade-enter .payment-sheet,
.sheet-fade-leave-to .payment-sheet {
  transform: translateY(105%);
}

@keyframes payment-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
